import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Radio,
  Tooltip,
  Stack
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { Robot } from '@api/bff/stationDevices';

type Props = {
  robots: Robot[];
  activeStationId: string | null;
  isBusy: boolean;
  onEdit: (robot: Robot) => void;
  onDelete: (id: string) => void;
  onSetActive: (stationId: string) => void;
};

const RobotList = ({
  robots,
  activeStationId,
  isBusy,
  onEdit,
  onDelete,
  onSetActive
}: Props) => {
  const { t } = useTranslation();

  if (robots.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 3, textAlign: 'center' }}
      >
        {t('settings:stationDevices.list.empty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {robots.map(robot => {
        const isActive = robot.stationId === activeStationId;
        return (
          <Card
            key={robot.id}
            variant="outlined"
            sx={{ borderColor: isActive ? 'primary.main' : 'divider' }}
          >
            <CardContent
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                '&:last-child': { pb: 1.5 }
              }}
            >
              <Tooltip title={t('settings:stationDevices.actions.setActive')}>
                <Radio
                  checked={isActive}
                  onChange={() => onSetActive(robot.stationId)}
                  disabled={isBusy}
                  size="small"
                />
              </Tooltip>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {robot.label || robot.stationId}
                  {isActive &&
                    ` · ${t('settings:stationDevices.actions.active')}`}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {robot.stationId} — {robot.deviceType}/{robot.mqttDeviceId}
                </Typography>
              </Box>
              <IconButton
                onClick={() => onEdit(robot)}
                disabled={isBusy}
                size="small"
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => onDelete(robot.id)}
                disabled={isBusy}
                size="small"
                color="error"
              >
                <Delete fontSize="small" />
              </IconButton>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export default RobotList;
